import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  addDoc,
  deleteDoc,
  Timestamp,
  deleteField,
} from 'firebase/firestore';
import { db, auth } from '../../firebase/firebaseConfig';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useNotifications } from '@toolpad/core';
import { ClientField, ClientFieldMultipleChoice, FieldType, FormTemplate, ReminderOption } from '../../../shared/types/appTypes';
import { REMINDER_OPTIONS } from '../../constants/appConstants';
import { createDefaultField } from './types';
import TemplateSidebar from './components/TemplateSidebar';
import FieldTypePalette from './components/FieldTypePalette';
import FormFieldList from './components/FormFieldList';

const ClientFormEditor = () => {
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const notifications = useNotifications();

  // Local editing state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editFields, setEditFields] = useState<ClientField[]>([]);
  const [editReminder, setEditReminder] = useState<ReminderOption | undefined>(undefined);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const templatesRef = collection(db, 'templateClientForms');
      const snapshot = await getDocs(templatesRef);

      const templateData: FormTemplate[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        templateData.push({
          id: docSnap.id,
          name: data.name || 'Untitled',
          description: data.description || '',
          fields: data.fields || [],
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          createdBy: data.createdBy || '',
          updatedBy: data.updatedBy || '',
        } as FormTemplate);
      });

      setTemplates(templateData);
    } catch (error) {
      console.error('Error fetching templates:', error);
      notifications.show('Failed to load templates', { severity: 'error', autoHideDuration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [notifications]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Sync local edit state when selected template changes
  useEffect(() => {
    if (selectedTemplate) {
      setEditName(selectedTemplate.name);
      setEditDescription(selectedTemplate.description || '');
      setEditFields(selectedTemplate.fields || []);
      setEditReminder(selectedTemplate.reminder);
      setHasChanges(false);
    }
  }, [selectedTemplate]);

  const handleSelectTemplate = (template: FormTemplate) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setSelectedTemplate(template);
  };

  const handleNewTemplate = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    const newTemplate: FormTemplate = {
      id: 'new',
      name: 'New Form Template',
      description: '',
      fields: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: auth.currentUser?.email || '',
      updatedBy: auth.currentUser?.email || '',
    };
    setSelectedTemplate(newTemplate);
    setEditName(newTemplate.name);
    setEditDescription('');
    setEditFields([]);
    setEditReminder(undefined);
    setHasChanges(true);
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'templateClientForms', id));
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      if (selectedTemplate?.id === id) {
        setSelectedTemplate(null);
        setEditFields([]);
      }
      notifications.show('Template deleted', { severity: 'success', autoHideDuration: 3000 });
    } catch (error) {
      console.error('Error deleting template:', error);
      notifications.show('Failed to delete template', { severity: 'error', autoHideDuration: 3000 });
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;

    try {
      setSaving(true);
      const now = Timestamp.now();
      const userEmail = auth.currentUser?.email || '';

      // Prepare fields with updated order and timestamps, without id
      const fieldsToSave = editFields.map((field, idx) => {
        const { ...fieldWithoutId } = field;
        return {
          ...fieldWithoutId,
          order: idx,
          modifiedAt: now,
          modifiedBy: userEmail,
        };
      });

      const templateData: any = {
        name: editName,
        description: editDescription,
        fields: fieldsToSave,
        updatedAt: now,
        updatedBy: userEmail,
      };

      // Only include reminder if it has a value
      if (editReminder) {
        templateData.reminder = editReminder;
      }

      let templateId = selectedTemplate.id;
      const isNew = selectedTemplate.id === 'new';

      if (isNew) {
        // Create new document with auto-generated ID
        const docRef = await addDoc(collection(db, 'templateClientForms'), {
          ...templateData,
          createdAt: now,
          createdBy: userEmail,
        });
        templateId = docRef.id;
      } else {
        // Update existing document
        await setDoc(doc(db, 'templateClientForms', templateId), templateData, {
          merge: true,
        });
      }

      // Update local state
      const updatedTemplate: FormTemplate = {
        id: templateId,
        ...templateData,
        fields: fieldsToSave,
        createdAt: isNew ? now : selectedTemplate.createdAt,
        createdBy: isNew ? userEmail : selectedTemplate.createdBy,
      };

      setTemplates((prev) => {
        if (isNew) {
          return [...prev, updatedTemplate];
        }
        return prev.map((t) => (t.id === templateId ? updatedTemplate : t));
      });

      setSelectedTemplate(updatedTemplate);
      setHasChanges(false);
      notifications.show('Template saved successfully', { severity: 'success', autoHideDuration: 3000 });
    } catch (error) {
      console.error('Error saving template:', error);
      notifications.show('Failed to save template', { severity: 'error', autoHideDuration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleAddField = (type: FieldType) => {
    const newField = createDefaultField(type, editFields.length);
    setEditFields((prev) => [...prev, newField]);
    setHasChanges(true);
  };

  const handleReorderFields = (startIndex: number, endIndex: number) => {
    const result = Array.from(editFields);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    // Update order property
    const reordered = result.map((field, idx) => ({ ...field, order: idx }));
    setEditFields(reordered);
    setHasChanges(true);
  };

  const handleUpdateField = (index: number, updates: Partial<ClientField>) => {
    setEditFields((prev) =>
      prev.map((field, i) =>
        i === index ? ({ ...field, ...updates } as ClientField) : field
      )
    );
    setHasChanges(true);
  };

  const handleDeleteField = (index: number) => {
    setEditFields((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleAddChoice = (fieldIndex: number) => {
    setEditFields((prev) =>
      prev.map((field, index) => {
        if (index !== fieldIndex || field.type !== 'multipleChoice') return field;
        const nextChoices = [...(field.choices || []), ''];
        return { ...field, choices: nextChoices } as ClientFieldMultipleChoice;
      })
    );
    setHasChanges(true);
  };

  const handleUpdateChoice = (fieldIndex: number, choiceIndex: number, value: string) => {
    setEditFields((prev) =>
      prev.map((field, index) => {
        if (index !== fieldIndex || field.type !== 'multipleChoice') return field;
        const nextChoices = [...(field.choices || [])];
        const previousChoiceValue = nextChoices[choiceIndex];
        nextChoices[choiceIndex] = value;
        const nextFieldValue = Array.isArray(field.value)
          ? field.value.map((selected) => (selected === previousChoiceValue ? value : selected))
          : field.value === previousChoiceValue
            ? value
            : field.value;
        return { ...field, choices: nextChoices, value: nextFieldValue } as ClientFieldMultipleChoice;
      })
    );
    setHasChanges(true);
  };

  const handleDeleteChoice = (fieldIndex: number, choiceIndex: number) => {
    setEditFields((prev) =>
      prev.map((field, index) => {
        if (index !== fieldIndex || field.type !== 'multipleChoice') return field;
        const deletedChoice = (field.choices || [])[choiceIndex];
        const nextChoices = (field.choices || []).filter((_, idx) => idx !== choiceIndex);
        const nextValue = Array.isArray(field.value)
          ? field.value.filter((selected) => selected !== deletedChoice)
          : field.value === deletedChoice
            ? null
            : field.value;
        return { ...field, choices: nextChoices, value: nextValue } as ClientFieldMultipleChoice;
      })
    );
    setHasChanges(true);
  };

  const handleReorderChoices = (fieldIndex: number, startIndex: number, endIndex: number) => {
    setEditFields((prev) =>
      prev.map((field, index) => {
        if (index !== fieldIndex || field.type !== 'multipleChoice') return field;
        const nextChoices = [...(field.choices || [])];
        const [removed] = nextChoices.splice(startIndex, 1);
        nextChoices.splice(endIndex, 0, removed);
        return { ...field, choices: nextChoices } as ClientFieldMultipleChoice;
      })
    );
    setHasChanges(true);
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Box sx={{ width: 280, flexShrink: 0, borderRight: 1, borderColor: 'divider' }}>
        <TemplateSidebar
          templates={templates}
          selectedId={selectedTemplate?.id || null}
          onSelect={handleSelectTemplate}
          onNew={handleNewTemplate}
          onDelete={handleDeleteTemplate}
          loading={loading}
        />
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {selectedTemplate ? (
            <>
              <TextField
                value={editName}
                onChange={(e) => {
                  setEditName(e.target.value);
                  setHasChanges(true);
                }}
                variant="standard"
                placeholder="Template Name"
                sx={{ flex: 1 }}
                inputProps={{ style: { fontSize: '1.25rem', fontWeight: 600 } }}
              />
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} /> : <Save />}
                onClick={handleSave}
                disabled={saving || !hasChanges}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <Typography variant="h6" color="text.secondary">
              Select or create a template
            </Typography>
          )}
        </Box>

        {/* Content Area */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : selectedTemplate ? (
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Field Builder */}
            <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
              <Stack spacing={2}>
                <TextField
                  label="Description"
                  value={editDescription}
                  onChange={(e) => {
                    setEditDescription(e.target.value);
                    setHasChanges(true);
                  }}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                />

                {/* Reminder Setting */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Default Reminder Timing</InputLabel>
                  <Select
                    value={editReminder || 'none'}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditReminder(value === 'none' ? undefined : value as ReminderOption);
                      setHasChanges(true);
                    }}
                    label="Default Reminder Timing"
                  >
                    {REMINDER_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Form Fields ({editFields.length})
                  </Typography>
                  {editFields.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No fields yet. Add fields from the palette on the right.
                    </Typography>
                  ) : (
                    <FormFieldList
                      fields={editFields}
                      templateId={selectedTemplate?.id || ''}
                      onReorder={handleReorderFields}
                      onReorderChoices={handleReorderChoices}
                      onAddChoice={handleAddChoice}
                      onUpdateChoice={handleUpdateChoice}
                      onDeleteChoice={handleDeleteChoice}
                      onUpdate={handleUpdateField}
                      onDelete={handleDeleteField}
                    />
                  )}
                </Paper>
              </Stack>
            </Box>

            {/* Field Palette */}
            <Box sx={{ width: 260, flexShrink: 0, p: 2, borderLeft: 1, borderColor: 'divider' }}>
              <FieldTypePalette onAddField={handleAddField} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography color="text.secondary">
              Select a template from the sidebar or create a new one
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ClientFormEditor;
